/**
 * demoProjectService.js - Service per gestire progetti demo
 * Sistema demo 100% database-driven, zero hardcoding
 */

import supabase from '../lib/supabase';

/**
 * Get template for a specific role
 * @param {string} role_professional - User's professional role
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const getTemplateForRole = async (role_professional) => {
  try {
    // Try exact role match
    let { data, error } = await supabase
      .from('project_templates')
      .select('*')
      .eq('role_professional', role_professional)
      .eq('is_active', true)
      .single();
    
    if (data && !error) {
      console.log('📋 Template found for role:', role_professional);
      return { data, error: null };
    }
    
    // Fallback to default template
    if (error?.code === 'PGRST116') {
      const { data: defaultData, error: defaultError } = await supabase
        .from('project_templates')
        .select('*')
        .eq('role_professional', 'default')
        .eq('is_active', true)
        .single();
      
      if (defaultData && !defaultError) {
        console.log('📋 Using default template');
        return { data: defaultData, error: null };
      }
    }
    
    return { data: null, error: error || new Error('No template found') };
  } catch (err) {
    console.error('📋 Template fetch error:', err);
    return { data: null, error: err };
  }
};

/**
 * Get all templates (for admin)
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllTemplates = async () => {
  try {
    const { data, error } = await supabase
      .from('project_templates')
      .select('*')
      .order('role_professional', { ascending: true });
    
    return { data: data || [], error };
  } catch (err) {
    console.error('📋 Get all templates error:', err);
    return { data: [], error: err };
  }
};

/**
 * Check if user has any projects
 * @param {string} userId - User's UUID
 * @returns {Promise<{hasProjects: boolean, projectCount: number, error: Error|null}>}
 */
export const checkUserHasProjects = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    if (error) throw error;
    
    return { 
      hasProjects: (count || 0) > 0, 
      projectCount: count || 0, 
      error: null 
    };
  } catch (err) {
    console.error('📋 Check projects error:', err);
    return { hasProjects: false, projectCount: 0, error: err };
  }
};

/**
 * Create demo project from template
 * @param {string} userId - User's UUID
 * @param {string} role_professional - User's professional role
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const createDemoProjectFromTemplate = async (userId, role_professional) => {
  try {
    // 1. Get template for role
    const { data: template, error: templateError } = await getTemplateForRole(role_professional);
    
    if (templateError || !template) {
      console.error('📋 No template available');
      return { data: null, error: templateError || new Error('No template found') };
    }
    
    // 2. Create project with is_demo = true
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .insert({
        user_id: userId,
        name: template.name,
        description: template.description,
        cover_image: template.cover_image,
        is_demo: true,
        template_id: template.id,
        status: 'active'
      })
      .select()
      .single();
    
    if (projectError) {
      console.error('📋 Create project error:', projectError);
      return { data: null, error: projectError };
    }
    
    // 3. Insert materials from template
    const materials = template.data?.materials || [];
    if (materials.length > 0) {
      const materialInserts = materials.map(mat => ({
        project_id: project.id,
        material_id: mat.material_id,
        material_name: mat.material_name,
        material_image: mat.material_image,
        material_type: mat.material_type,
        notes: mat.notes || mat.application
      }));
      
      const { error: materialsError } = await supabase
        .from('project_materials')
        .insert(materialInserts);
      
      if (materialsError) {
        console.warn('📋 Materials insert warning:', materialsError);
        // Don't fail the whole operation for materials error
      }
    }
    
    console.log('📋 Demo project created:', project.id);
    return { data: project, error: null };
    
  } catch (err) {
    console.error('📋 Create demo project error:', err);
    return { data: null, error: err };
  }
};

/**
 * Convert demo project to real project (is_demo = false)
 * Called when user makes any modification
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const convertDemoToReal = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({ 
        is_demo: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', projectId)
      .select()
      .single();
    
    if (error) throw error;
    
    console.log('📋 Demo converted to real project:', projectId);
    return { data, error: null };
  } catch (err) {
    console.error('📋 Convert demo error:', err);
    return { data: null, error: err };
  }
};

/**
 * Duplicate a project (demo or real)
 * @param {string} projectId - Project UUID to duplicate
 * @param {string} userId - User's UUID
 * @param {string} newName - Optional new name
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const duplicateProject = async (projectId, userId, newName = null) => {
  try {
    // 1. Get original project
    const { data: original, error: fetchError } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();
    
    if (fetchError || !original) {
      throw fetchError || new Error('Project not found');
    }
    
    // 2. Create duplicate (always as non-demo)
    const { data: duplicate, error: createError } = await supabase
      .from('projects')
      .insert({
        user_id: userId,
        name: newName || `${original.name} (Copy)`,
        description: original.description,
        cover_image: original.cover_image,
        client: original.client,
        is_demo: false, // Duplicates are always real projects
        template_id: original.template_id,
        status: 'active'
      })
      .select()
      .single();
    
    if (createError) throw createError;
    
    // 3. Duplicate materials
    const { data: materials } = await supabase
      .from('project_materials')
      .select('*')
      .eq('project_id', projectId);
    
    if (materials && materials.length > 0) {
      const materialDuplicates = materials.map(mat => ({
        project_id: duplicate.id,
        material_id: mat.material_id,
        material_name: mat.material_name,
        material_image: mat.material_image,
        material_type: mat.material_type,
        notes: mat.notes
      }));
      
      await supabase
        .from('project_materials')
        .insert(materialDuplicates);
    }
    
    console.log('📋 Project duplicated:', duplicate.id);
    return { data: duplicate, error: null };
    
  } catch (err) {
    console.error('📋 Duplicate project error:', err);
    return { data: null, error: err };
  }
};

/**
 * Get user's projects with demo flag
 * @param {string} userId - User's UUID
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getUserProjects = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_materials (
          id,
          material_id,
          material_name,
          material_image,
          material_type,
          notes
        )
      `)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
    
    return { data: data || [], error };
  } catch (err) {
    console.error('📋 Get user projects error:', err);
    return { data: [], error: err };
  }
};

/**
 * Update template (admin only)
 * @param {string} templateId - Template UUID
 * @param {Object} payload - Update data
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const updateTemplate = async (templateId, payload) => {
  try {
    const { data, error } = await supabase
      .from('project_templates')
      .update({
        ...payload,
        updated_at: new Date().toISOString()
      })
      .eq('id', templateId)
      .select()
      .single();
    
    return { data, error };
  } catch (err) {
    console.error('📋 Update template error:', err);
    return { data: null, error: err };
  }
};

/**
 * Create new template (admin only)
 * @param {Object} payload - Template data
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const createTemplate = async (payload) => {
  try {
    const { data, error } = await supabase
      .from('project_templates')
      .insert(payload)
      .select()
      .single();
    
    return { data, error };
  } catch (err) {
    console.error('📋 Create template error:', err);
    return { data: null, error: err };
  }
};

/**
 * Delete template (admin only)
 * @param {string} templateId - Template UUID
 * @returns {Promise<{error: Error|null}>}
 */
export const deleteTemplate = async (templateId) => {
  try {
    const { error } = await supabase
      .from('project_templates')
      .delete()
      .eq('id', templateId);
    
    return { error };
  } catch (err) {
    console.error('📋 Delete template error:', err);
    return { error: err };
  }
};

export default {
  getTemplateForRole,
  getAllTemplates,
  checkUserHasProjects,
  createDemoProjectFromTemplate,
  convertDemoToReal,
  duplicateProject,
  getUserProjects,
  updateTemplate,
  createTemplate,
  deleteTemplate
};
